import { useState } from 'react';
import SectionTitle from '../../../components/atoms/SectionTitle';
import SectionDesc from '../../../components/atoms/SectionDesc';
import CustomModal from '../../../components/atoms/CustomModal';
import Input from '../../../components/atoms/Input';
import Button from '../../../components/atoms/Button';
import { useTranslation } from 'react-i18next';
import YadLogo from '../../../assets/icons/YadLogo';
import InfoText from '../../../components/atoms/InfoText';
import CoinsSection from './CoinsSection';
import FreeServicesSection from './FreeServicesSection';
import {
  useGetAllCoinsDeductionsQuery,
  useGetServiceLimitConfigsQuery,
  useUpdateCoinsServiceMutation,
  useUpdateServiceLimitConfigMutation,
} from '../../../store/services/coinsControl';
import { message } from 'antd';

export default function CoinsServiceLimits() {
  const { t } = useTranslation();
  
  const { data: coinsResponse, isLoading: isLoadingCoins } = useGetAllCoinsDeductionsQuery();
  const { data: freeServicesResponse, isLoading: isLoadingFree } = useGetServiceLimitConfigsQuery();
  
  const [updateCoinsService, { isLoading: isUpdatingCoins }] = useUpdateCoinsServiceMutation();
  const [updateServiceLimit, { isLoading: isUpdatingFree }] = useUpdateServiceLimitConfigMutation();

  const [selectedItem, setSelectedItem] = useState<{ id: string; name: string; amount: number; type: 'coins' | 'free' } | null>(null);
  const [amount, setAmount] = useState('');

  const handleSave = async () => {
    if (!selectedItem) return;

    if (!amount || amount.toString().trim() === '') {
      message.error(t('components.requiredField'));
      return;
    }

    try {
      if (selectedItem.type === 'coins') {
        await updateCoinsService({
          id: selectedItem.id,
          body: { coinsNo: Number(amount) },
        }).unwrap();
      } else {
        await updateServiceLimit({
          id: selectedItem.id,
          body: { limitNo: Number(amount) },
        }).unwrap();
      }
      setSelectedItem(null);
    } catch (error: any) {
      const errData = error?.data;
      const errorDetail = errData?.errors ? Object.values(errData.errors)[0] as string : null;
      message.error(errorDetail || errData?.message || t('common.error', 'Failed to update'));
    }
  };

  const coinsData = coinsResponse?.payload || [];
  const freeData = freeServicesResponse?.payload || [];

  const isFreeService = selectedItem?.type === 'free';
  const isUpdating = isUpdatingCoins || isUpdatingFree;

  return (
    <div className="flex flex-col gap-6">
      {/* Page Header */}
      <div className="flex flex-col gap-1">
        <SectionTitle text={t('home.coins-service-limits')} />
        <SectionDesc text={t('home.coins-service-limits-desc')} />
      </div>

      <CoinsSection
        data={coinsData}
        isLoading={isLoadingCoins}
        onEdit={(item) => {
          setSelectedItem({ id: item.id, name: item.name, amount: item.coinsNo, type: 'coins' });
          setAmount(item.coinsNo.toString());
        }}
      />
      <FreeServicesSection
        data={freeData}
        isLoading={isLoadingFree}
        onEdit={(item) => {
          setSelectedItem({ id: item.id, name: item.name, amount: item.limitNo, type: 'free' });
          setAmount(item.limitNo.toString());
        }}
      />

      <CustomModal
        isOpen={!!selectedItem}
        onClose={() => setSelectedItem(null)}
        title={
          !isFreeService
            ? t('home.coins-amount-title')
            : t('home.service-limits-title')
        }
        subTitle={!isFreeService
            ? t('home.coins-amount-desc')
            : t('home.service-limits-desc')}
        width={600}
        closeIconClassName="[&_.ant-modal-close]:!top-8"
      >
        <div className="flex flex-col gap-6 mt-4">
          <Input
            id="amount"
            label={!isFreeService ? t('home.amount') : t('home.limit')}
            placeholder={t('home.set-number')}
            value={amount}
            type="number"
            onChange={(e) => setAmount(e.target.value)}
            className="!rounded-xl"
          />
          <div className="flex justify-end mt-2">
            <Button
              variant="dark"
              loading={isUpdating}
              onClick={handleSave}
              className="rounded-xl"
            >
              {t('home.done')}
            </Button>
          </div>
        </div>
      </CustomModal>
    </div>
  );
}
